import { useState } from "react";
import { Loader2, Check, FileText, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { studio, apiError, inputCls, btnPrimary, btnGhost } from "@/lib/studioApi";
import { tr } from "@/i18n/appLang";

const Toggle = ({ testId, checked, onChange, label }) => (
  <label className="flex items-center gap-2 text-sm text-[#0F2645]">
    <input type="checkbox" data-testid={testId} checked={checked} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 accent-[#3A5CC8]" />
    {label}
  </label>
);

export const CgvEditor = ({ profile, setProfile, onSave, busy }) => {
  const [loadingTpl, setLoadingTpl] = useState(false);
  const set = (k) => (v) => setProfile((p) => ({ ...p, [k]: v }));
  const text = profile.cgv_text || "";

  const insertTemplate = async () => {
    if (text.trim() && !window.confirm(tr("Remplacer vos conditions actuelles par le modèle de votre pays ?"))) return;
    setLoadingTpl(true);
    try {
      const { data } = await studio.get("/documents/cgv-template");
      set("cgv_text")(data.text);
      toast.success("Modèle inséré : adaptez-le puis enregistrez.");
    } catch (e) { toast.error(apiError(e, "Modèle indisponible.")); }
    finally { setLoadingTpl(false); }
  };

  return (
    <div data-testid="documents-cgv" className="dv-card-in mt-4 rounded-2xl border border-[#E2E7F7] bg-white p-5 shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-base font-semibold text-[#0F2645] inline-flex items-center gap-2"><FileText className="h-4 w-4 text-[#3A5CC8]" /> Conditions générales de vente</h2>
          <p className="mt-1 text-xs text-[#5C6A87]">Ajoutées en dernière page de vos devis et factures PDF, et visibles sur les pages en ligne. Une ligne commençant par « Article » devient un titre.</p>
        </div>
        <button type="button" data-testid="cgv-template-btn" disabled={loadingTpl} onClick={insertTemplate} className={btnGhost}>
          {loadingTpl ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Insérer le modèle de mon pays
        </button>
      </div>
      <div className="mt-4 grid gap-4 sm:grid-cols-[1fr_auto] sm:items-end">
        <div>
          <label className="text-xs font-bold uppercase tracking-wide text-[#5C6A87]">Titre</label>
          <input data-testid="cgv-title" className={`${inputCls} mt-1`} value={profile.cgv_title ?? "Conditions Générales de Vente"} onChange={(e) => set("cgv_title")(e.target.value)} />
        </div>
        <div className="flex flex-wrap gap-4 pb-2">
          <Toggle testId="cgv-on-quotes" checked={profile.cgv_on_quotes !== false} onChange={set("cgv_on_quotes")} label="Sur les devis" />
          <Toggle testId="cgv-on-invoices" checked={profile.cgv_on_invoices !== false} onChange={set("cgv_on_invoices")} label="Sur les factures" />
        </div>
      </div>
      <textarea
        data-testid="cgv-text"
        rows={18}
        className={`${inputCls} mt-3 font-mono text-[12px] leading-relaxed`}
        placeholder={"Article 1 – Champ d'application\nLes présentes conditions s'appliquent à…\n\nArticle 2 – Paiement\n…"}
        value={text}
        onChange={(e) => set("cgv_text")(e.target.value)}
      />
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <p data-testid="cgv-count" className="text-xs text-[#93A0BC]">{text.length.toLocaleString("fr-BE")} caractères · {text.split(/\n/).filter((l) => /^(article|artikel|artículo|art\.|#)/i.test(l.trim())).length} articles</p>
        <button type="button" data-testid="cgv-save" disabled={busy} onClick={onSave} className={btnPrimary}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Enregistrer les CGV
        </button>
      </div>
    </div>
  );
};
